// Artículos sin movimiento por bodega: los que tienen stock, nunca se vendieron
// en esa bodega y llevan más de N días desde su último ingreso. El corte por
// antigüedad del ingreso deja fuera la mercadería recién llegada, que todavía
// no tuvo tiempo de venderse. Los clasificadores elegidos salen como columna
// y, si se les fija un tipo, además filtran.
// El resultado suele pasar de las 100.000 filas, así que la tabla va paginada
// y la descarga completa la sirve el backend como CSV en streaming.
import React, { useState, useCallback } from "react";
import { CalendarOff, Download, Info } from "lucide-react";
import {
  COLORS, API_BASE, apiFetch, inputStyle, labelStyle, btnPrimary, btnSecondary,
  thStyle, tdStyle, mono, cardStyle, tableWrap, PageHeader, EmptyState,
  MetricCard, useMoneda,
} from "../shared/ui";
import { fmtMoneda } from "../shared/moneda";
import MultiClassifierFilter from "../shared/MultiClassifierFilter";
import BodegaMultiSelect from "../shared/BodegaMultiSelect";
import { construirParams, textoSinVenta, textoSinIngreso } from "./sinMovimiento";

const RUTA = "/bi/articulos-sin-movimiento";
const POR_PAGINA = 100;

export default function SinMovimientoPage({ token }) {
  const { simbolo } = useMoneda();
  const [dias, setDias] = useState("90");
  const [bodegas, setBodegas] = useState([]);
  const [clas, setClas] = useState([]);
  const [sinIngreso, setSinIngreso] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [error, setError] = useState(null);

  const onClasChange = useCallback((rows) => setClas(rows), []);

  // Un campo vacío no vale 90: mandar días=0 traería todo el inventario.
  const motivoInvalido = (() => {
    if (dias.trim() === "") return "Indique los días desde el último ingreso";
    const n = Number(dias);
    if (!Number.isInteger(n)) return "Los días deben ser un número entero";
    if (n < 1) return "Los días deben ser al menos 1";
    if (n > 1830) return "El máximo son 1830 días (5 años)";
    return null;
  })();

  const consultar = async (nuevaPagina = 1) => {
    if (cargando || motivoInvalido) return;
    setError(null);
    setCargando(true);
    try {
      const filtros = { dias: Number(dias), bodegas, clas, incluirSinIngreso: sinIngreso,
                        porPagina: POR_PAGINA };
      // El total solo se reusa si los filtros no cambiaron desde que se contó.
      const clave = construirParams({ ...filtros, pagina: 1 });
      const reusarTotal = nuevaPagina > 1 && datos?.clave === clave;
      const qs = construirParams({ ...filtros, pagina: nuevaPagina, contar: !reusarTotal });
      const res = await apiFetch(`${RUTA}?${qs}`, {}, token);
      setDatos({ ...res, total: reusarTotal ? datos.total : res.total, clave });
      setPagina(nuevaPagina);
    } catch (e) {
      setError(e.message);
      setDatos(null);
    }
    setCargando(false);
  };

  // La descarga no puede ir por apiFetch (devuelve JSON) ni por un <a href>
  // suelto (no lleva el Bearer): se baja como blob y se dispara el guardado.
  const descargar = async () => {
    if (descargando || motivoInvalido) return;
    setError(null);
    setDescargando(true);
    try {
      const qs = construirParams({
        dias: Number(dias), bodegas, clas, formato: "csv", incluirSinIngreso: sinIngreso,
      });
      const res = await fetch(`${API_BASE}${RUTA}?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Error ${res.status} al generar el CSV`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `articulos_sin_movimiento_${dias}d.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    }
    setDescargando(false);
  };

  const columnas = datos?.columnas || [];
  const totalPaginas = datos ? Math.max(1, Math.ceil(datos.total / POR_PAGINA)) : 1;

  const horizonte = datos?.horizonte;

  const celda = (col, valor) => {
    if (valor == null) {
      // Un vacío acá no es "nunca se vendió": es que la venta cae fuera del
      // histórico que el ERP tiene cargado.
      if (col.campo === "ultima_venta") return textoSinVenta(horizonte);
      // Ni "entró hoy": es que el ERP no emite ingresos para esa bodega.
      if (col.campo === "fecha_ultimo_ingreso") return textoSinIngreso();
      return "—";
    }
    if (col.tipo === "dinero") return fmtMoneda(valor, simbolo);
    if (col.tipo === "numero") return Number(valor).toLocaleString("es");
    return valor;
  };

  return (
    <div>
      <PageHeader
        title="Artículos sin movimiento"
        subtitle="Artículos con stock que nunca se vendieron en la bodega y llevan más de N días desde su último ingreso"
      />

      <div style={{ ...cardStyle, marginBottom: 20, display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ ...labelStyle, marginBottom: 2 }}>
            <CalendarOff size={12} style={{ marginRight: 4 }} />Días desde el último ingreso
          </label>
          <input
            type="number" min="1" max="1830" value={dias}
            onChange={e => setDias(e.target.value)}
            style={{ ...inputStyle, width: 120 }}
          />
        </div>

        <BodegaMultiSelect
          token={token} label="Bodegas" seleccion={bodegas} onChange={setBodegas}
        />

        <div style={{ display: "flex", flexDirection: "column", gap: 6, maxWidth: 230 }}>
          <label style={{ ...labelStyle, marginBottom: 2 }}>Sin fecha de ingreso</label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: COLORS.text, cursor: "pointer" }}>
            <input
              type="checkbox" checked={sinIngreso}
              onChange={e => setSinIngreso(e.target.checked)}
            />
            Incluir artículos sin fecha de ingreso
          </label>
          <p style={{ color: COLORS.textDim, fontSize: 12, margin: 0 }}>
            Bodegas para las que el ERP no emite documento de ingreso. Sin esto no aparecen.
          </p>
        </div>

        <div style={{ minWidth: 320 }}>
          <MultiClassifierFilter token={token} onChange={onClasChange} />
          <p style={{ color: COLORS.textDim, fontSize: 12, margin: "6px 0 0" }}>
            Cada clasificador agrega una columna. Si además elige un tipo, filtra.
          </p>
        </div>

        <div style={{ display: "flex", gap: 8, alignSelf: "flex-end" }}>
          <button
            onClick={() => consultar(1)} disabled={cargando || !!motivoInvalido}
            title={motivoInvalido || ""}
            style={{ ...btnPrimary, opacity: cargando || motivoInvalido ? 0.6 : 1 }}
          >{cargando ? "Consultando..." : "Consultar"}</button>
          <button
            onClick={descargar} disabled={descargando || !!motivoInvalido}
            title={motivoInvalido || "Descarga el reporte completo, no solo la página mostrada"}
            style={{ ...btnSecondary, opacity: descargando || motivoInvalido ? 0.6 : 1, display: "flex", alignItems: "center", gap: 6 }}
          ><Download size={14} />{descargando ? "Generando..." : "Descargar CSV"}</button>
        </div>
      </div>

      {motivoInvalido && (
        <div style={{ marginBottom: 16, padding: "10px 16px", borderRadius: 8, background: COLORS.warningSoft, color: COLORS.warning, fontSize: 13 }}>
          {motivoInvalido}
        </div>
      )}
      {error && (
        <div style={{ marginBottom: 16, padding: "10px 16px", borderRadius: 8, background: COLORS.dangerSoft, color: COLORS.danger, fontSize: 13 }}>
          {error}
        </div>
      )}

      {horizonte?.desde && (
        <div style={{ marginBottom: 16, padding: "10px 16px", borderRadius: 8, background: COLORS.surfaceHover, color: COLORS.textMuted, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
          <Info size={14} />
          Histórico de ventas cargado desde el <strong>{horizonte.desde}</strong> ({horizonte.dias} días).
          “Nunca vendido” significa sin ventas dentro de esa ventana: un artículo vendido
          antes del {horizonte.desde} igual aparece en el reporte.
        </div>
      )}

      {datos && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 20 }}>
            <MetricCard icon={CalendarOff} label="Artículos sin movimiento" value={datos.total.toLocaleString("es")} sub={`ingresados hace más de ${datos.dias} días`} color={COLORS.warning} />
            <MetricCard icon={Download} label="Página" value={`${pagina} de ${totalPaginas.toLocaleString("es")}`} sub={`${POR_PAGINA} por página`} color={COLORS.primary} />
            <MetricCard icon={CalendarOff} label="Bodegas filtradas" value={bodegas.length || "Todas"} color={COLORS.accent} />
          </div>

          {datos.filas.length === 0 ? (
            <EmptyState message="No hay artículos sin movimiento con esos criterios" />
          ) : (
            <>
              <div style={tableWrap}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: COLORS.surfaceHover }}>
                      {columnas.map(c => <th key={c.campo} style={thStyle}>{c.etiqueta}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {datos.filas.map((f, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                        {columnas.map(c => (
                          <td key={c.campo} style={{
                            ...tdStyle,
                            ...(c.tipo === "numero" || c.tipo === "dinero" ? mono : {}),
                            ...(f[c.campo] == null ? { color: COLORS.textDim } : {}),
                          }}>
                            {celda(c, f[c.campo])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 14 }}>
                <button
                  onClick={() => consultar(pagina - 1)} disabled={cargando || pagina <= 1}
                  style={{ ...btnSecondary, opacity: pagina <= 1 ? 0.5 : 1 }}
                >← Anterior</button>
                <span style={{ fontSize: 13, color: COLORS.textMuted }}>
                  Página {pagina.toLocaleString("es")} de {totalPaginas.toLocaleString("es")}
                </span>
                <button
                  onClick={() => consultar(pagina + 1)} disabled={cargando || pagina >= totalPaginas}
                  style={{ ...btnSecondary, opacity: pagina >= totalPaginas ? 0.5 : 1 }}
                >Siguiente →</button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
